// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  NavigationItemProps,
  NavigationItem,
  NavigationList,
  Link,
  Eyebrow,
} from '@squareup/dex-ui';
import { Display20, Paragraph30, Box } from '@squareup/dex-ui-shared-base';
import { Grid } from '@squareup/dex-ui-shared-grid';
import { TrackedBox } from '@squareup/dex-ui-user-interaction-publisher';
import React, { ReactNode } from 'react';

import styles from './reference-page.module.css';

interface ApiAside {
  navigationItems?: NavigationItemProps[] | undefined;
  content: ReactNode;
  trackingId: string;
}

interface ReferencePageProps {
  eyebrow?: ReactNode | undefined;
  title?: ReactNode | undefined;
  subTitle?: ReactNode | undefined;
  description?: ReactNode | undefined;
  footer?: ReactNode | undefined;
  aside?: ApiAside | undefined;
  backHref?: React.ComponentProps<typeof Link>['href'] | undefined;
  disableVersionSwitcherDesktop?: boolean;
}

/**
 * This component is the common layout and styling for all reference api pages.
 */
const ReferencePage = ({
  eyebrow,
  title,
  subTitle,
  description,
  footer,
  aside,
  backHref,
  disableVersionSwitcherDesktop = false,
}: ReferencePageProps) => {
  const AsideContent = ({
    aside,
    versionSwitcher,
  }: {
    aside: ApiAside;
    versionSwitcher: ReactNode;
  }) => {
    const { trackingId, navigationItems, content } = aside;
    const versionSwitcherUi = versionSwitcher ? (
      <Box className={styles['desktop']}>{versionSwitcher}</Box>
    ) : null;
    return (
      <TrackedBox trackingId={trackingId}>
        {navigationItems && navigationItems.length > 0 ? (
          <Box className={styles['navigation-aside']}>
            <NavigationList isHorizontal={true}>
              {navigationItems.map((item) => {
                return (
                  <NavigationItem
                    key={item.testId}
                    {...item}
                    verticalMargin={false}
                  ></NavigationItem>
                );
              })}
            </NavigationList>
            {versionSwitcherUi}
          </Box>
        ) : (
          versionSwitcherUi
        )}
        {content}
      </TrackedBox>
    );
  };

  const versionSwitcher = <SpecVersionPickerContainer variant="reference" />;

  const render = () => {
    return (
      <>
        <section className={styles.content}>
          <Box className={styles.eyebrow} margin={{ bottom: '1x' }}>
            <Eyebrow backHref={backHref}>{eyebrow}</Eyebrow>
            <Box className={styles['narrow']}>{versionSwitcher}</Box>
          </Box>
          <Display20>{title}</Display20>
          <Box margin={{ bottom: '2x', top: '4x' }}>
            <Paragraph30>{subTitle}</Paragraph30>
          </Box>
          {description}
          {footer}
        </section>
        <aside data-testid="reference-page-aside" className={styles.aside}>
          {aside && (
            <AsideContent
              aside={aside}
              versionSwitcher={
                !disableVersionSwitcherDesktop ? versionSwitcher : null
              }
            ></AsideContent>
          )}
        </aside>
      </>
    );
  };

  return <Grid variant="content" render={render} />;
};

export { ReferencePage, type ApiAside, type ReferencePageProps };
